import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cloud,
  Server,
  Database,
  Zap,
  CheckCircle,
  ArrowRight,
  Users,
  TrendingUp,
  Target,
  BarChart3,
  Shield,
  Settings,
  Play,
  Download,
  Star,
  Clock,
  DollarSign,
  Building2,
  Cpu,
  Lock,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  ExternalLink,
  Lightbulb,
  PieChart,
  Activity,
  Wifi,
  Globe,
  Monitor,
  Smartphone,
  Globe2,
  CloudRain,
  CloudLightning,
  CloudSnow,
  CloudFog,
  CloudDrizzle,
  CloudOff,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import CloudFirstBg from '../components/assets/cloud-first.jpg';
import CloudFirstBg2 from '../components/assets/cloud2.jpg';
import CloudFirstBg3 from '../components/assets/cloud3.jpg';
import CommonContactUs from "./CommonContactUs";
import CloudHeroSlide from "../components/hero/CloudHeroSlide";

const CloudFirstPage = (props) => {
  const [activeTab, setActiveTab] = useState("overview");

  const services = [
    {
      icon: <Cloud className="w-8 h-8 text-blue-600" />,
      title: "Cloud Migration",
      description:
        "Seamlessly migrate your applications and infrastructure to the cloud with minimal downtime.",
      features: [
        "Legacy system migration",
        "Data migration",
        "Application modernization",
        "Performance optimization",
      ],
    },
    {
      icon: <Server className="w-8 h-8 text-green-600" />,
      title: "Cloud Infrastructure",
      description:
        "Design and deploy scalable cloud infrastructure that grows with your business needs.",
      features: [
        "Auto-scaling",
        "Load balancing",
        "High availability",
        "Disaster recovery",
      ],
    },
    {
      icon: <Database className="w-8 h-8 text-purple-600" />,
      title: "Cloud Databases",
      description:
        "Managed database solutions with built-in scalability, security, and performance optimization.",
      features: [
        "Managed databases",
        "Data replication",
        "Backup & recovery",
        "Performance tuning",
      ],
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-600" />,
      title: "Serverless Computing",
      description:
        "Build and deploy applications without managing servers using cloud-native services.",
      features: [
        "Function-as-a-Service",
        "Event-driven architecture",
        "Pay-per-use pricing",
        "Auto-scaling",
      ],
    },
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Scalability",
      description:
        "Scale your infrastructure up or down based on demand automatically.",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-green-600" />,
      title: "Cost Efficiency",
      description:
        "Pay only for what you use with flexible pricing models and resource optimization.",
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Security",
      description:
        "Enterprise-grade security with built-in compliance and data protection.",
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-600" />,
      title: "Global Reach",
      description:
        "Deploy applications globally with low latency and high availability.",
    },
  ];

  const cloudProviders = [
    {
      name: "Amazon Web Services (AWS)",
      description:
        "Comprehensive cloud platform with extensive service offerings",
      icon: <Cloud className="w-6 h-6" />,
      features: [
        "EC2, S3, Lambda",
        "RDS, DynamoDB",
        "CloudFront, Route 53",
        "IAM, CloudWatch",
      ],
    },
    {
      name: "Microsoft Azure",
      description:
        "Enterprise cloud platform with strong integration capabilities",
      icon: <Cloud className="w-6 h-6" />,
      features: [
        "Virtual Machines",
        "Azure SQL",
        "Functions, Logic Apps",
        "Active Directory",
      ],
    },
    {
      name: "Google Cloud Platform (GCP)",
      description:
        "Innovative cloud services with advanced AI and ML capabilities",
      icon: <Cloud className="w-6 h-6" />,
      features: [
        "Compute Engine",
        "Cloud SQL",
        "Cloud Functions",
        "BigQuery, AI/ML",
      ],
    },
    {
      name: "Multi-Cloud Strategy",
      description:
        "Distribute workloads across multiple cloud providers for optimal performance",
      icon: <Globe className="w-6 h-6" />,
      features: [
        "Risk mitigation",
        "Cost optimization",
        "Best-of-breed services",
        "Vendor independence",
      ],
    },
  ];

  const testimonials = [
    {
      name: "David Wilson",
      role: "CTO",
      company: "CloudTech Solutions",
      content:
        "TechMind's cloud migration strategy reduced our infrastructure costs by 60% while improving performance by 300%.",
      rating: 5,
    },
    {
      name: "Lisa Chen",
      role: "VP of Engineering",
      company: "Digital Innovations",
      content:
        "The serverless architecture implementation was seamless and our development velocity increased dramatically.",
      rating: 5,
    },
    {
      name: "Robert Johnson",
      role: "Infrastructure Manager",
      company: "Global Enterprises",
      content:
        "Their multi-cloud strategy helped us achieve 99.99% uptime and reduced our operational overhead significantly.",
      rating: 5,
    },
  ];

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderImages = [CloudFirstBg, CloudFirstBg2, CloudFirstBg3];

  const goToPrev = () => setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  const goToNext = () => setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));

   // Auto-advance slider every 4 seconds
    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentSlide((prev) =>
          prev === sliderImages.length - 1 ? 0 : prev + 1
        );
      }, 8000);
      return () => clearInterval(interval);
    }, [sliderImages.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Slider */}
      {props?.from==='Home' ?null:
      <section className="relative bg-[#1A193B] h-[716px] text-white overflow-hidden pt-24">
        <div className="absolute inset-0 bg-black/20 z-10"></div>
        {/* Slider Images */}
        <div className="absolute inset-0 w-full h-full flex transition-all duration-700 z-0">
          {sliderImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt="Cloud First Slide"
              className={`object-cover w-full h-full absolute top-0 left-0 transition-opacity duration-700 ${currentSlide === idx ? 'opacity-70 z-20' : 'opacity-0 z-0'}`}
              style={{ transition: 'opacity 0.7s' }}
            />
          ))}
        </div>
        {/* Slider Controls */}
        <div className="absolute left-0 right-0 flex justify-between items-center h-full px-4 z-30 pointer-events-none">
          <button
            onClick={goToPrev}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Previous Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={goToNext}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Next Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30" style={{zIndex:'42'}}>
          {sliderImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx) }
              className={`w-3 h-3 rounded-full ${currentSlide === idx ? 'bg-white' : 'bg-white/40'} border border-white transition-all`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        {/* Hero Content */}
        {/* <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-56 z-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Cloud First</h1>
            <p className="text-lg md:text-xl lg:text-2xl mb-8 text-indigo-100 font-medium">
              Embrace cloud-native solutions for scalable, secure, and
              cost-effective digital transformation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-3 bg-white text-indigo-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Get Started
              </Link>
              <Link
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-indigo-600 transition-colors font-medium"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div> */}
        <CloudHeroSlide/>
      </section>
      }

      {/* Benefits Section */}
      <section id='learn-more-cloud' className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Cloud First?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leverage the power of cloud computing to drive innovation and
              business growth
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Cloud First Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cloud solutions designed for modern business
              requirements
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center text-gray-600"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Providers Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Cloud Platforms We Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expertise across major cloud providers and multi-cloud strategies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cloudProviders.map((provider, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg mr-4">
                    {provider.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {provider.name}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">{provider.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {provider.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="text-sm text-gray-600 bg-white p-2 rounded"
                    >
                      {feature}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Architecture Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Cloud Architecture Patterns
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven architectural patterns for building scalable and resilient
              cloud applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Microservices",
                description:
                  "Break down applications into small, independent services for better scalability and maintainability",
                icon: <Server className="w-8 h-8 text-blue-600" />,
              },
              {
                title: "Event-Driven",
                description:
                  "Build responsive systems that react to events and maintain loose coupling between components",
                icon: <Zap className="w-8 h-8 text-green-600" />,
              },
              {
                title: "Serverless",
                description:
                  "Focus on business logic while the cloud provider manages infrastructure and scaling",
                icon: <Cloud className="w-8 h-8 text-purple-600" />,
              },
            ].map((pattern, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-center mb-4">{pattern.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {pattern.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {pattern.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from clients who have successfully embraced cloud-first
              strategies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {testimonial.role} at {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Go Cloud First?
            </h2>
            <p className="text-xl mb-8 text-indigo-100">
              Transform your infrastructure with cloud-native solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-3 bg-white text-indigo-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Start Your Cloud Journey
              </Link>
              <Link
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-indigo-600 transition-colors font-medium"
              >
                Explore Cloud Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}


    </div>
  );
};

export default CloudFirstPage;
